import { useState } from "react";
import { MessageCircle, Star, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Floating feedback button. Visible app-wide. Mobile-safe: positioned bottom-right
 * with extra bottom inset so it never collides with the blog post share-bar.
 */
export default function FeedbackButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [worked, setWorked] = useState("");
  const [confusing, setConfusing] = useState("");
  const [request, setRequest] = useState("");

  if (!user) return null; // require login to leave feedback (keeps data clean)

  const submit = async () => {
    if (!rating) {
      toast.error("Pick a star rating first.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        rating,
        feedback_type: "general",
        worked_well: worked,
        confusing,
        feature_request: request,
        comment: [worked, confusing, request].filter(Boolean).join("\n\n"),
      });
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
        setRating(0); setWorked(""); setConfusing(""); setRequest("");
      }, 1500);
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("You're sending feedback fast! Please wait a few minutes and try again.");
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't submit feedback");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Share feedback"
        data-testid="feedback-fab"
        className="fixed bottom-20 right-5 z-[60] md:bottom-6 md:right-6
                   inline-flex items-center gap-2 rounded-full bg-spark-purple text-white
                   px-4 py-3 shadow-lg hover:scale-[1.03] active:scale-100 transition"
        style={{ boxShadow: "0 10px 30px -8px rgba(91,77,255,0.45)" }}
      >
        <MessageCircle size={18} />
        <span className="hidden sm:inline text-sm font-semibold">Feedback</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] grid place-items-end sm:place-items-center bg-black/40 backdrop-blur-sm p-3"
          role="dialog"
          aria-modal="true"
          data-testid="feedback-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-full sm:max-w-md rounded-2xl bg-background border border-border p-5 sm:p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-spark-purple">Feedback</div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="feedback-close"
                      className="grid h-8 w-8 place-items-center rounded-full hover:bg-secondary">
                <X size={16} />
              </button>
            </div>

            {sent ? (
              <div className="py-8 text-center" data-testid="feedback-success">
                <CheckCircle2 className="mx-auto text-emerald-500" size={36} />
                <div className="mt-3 text-base font-semibold">Thank you!</div>
                <div className="text-sm text-muted-foreground">We read every note.</div>
              </div>
            ) : (
              <>
                <h2 className="mt-1 text-xl font-bold">Rate your experience</h2>
                <p className="mt-1 text-sm text-muted-foreground">Honest feedback shapes what ships next.</p>

                <div className="mt-4 flex items-center gap-1" data-testid="feedback-stars">
                  {[1,2,3,4,5].map((n) => {
                    const active = (hover || rating) >= n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setRating(n)}
                        aria-label={`${n} stars`}
                        data-testid={`feedback-star-${n}`}
                        className="p-1"
                      >
                        <Star size={26}
                          className={active ? "fill-amber-400 stroke-amber-500" : "stroke-muted-foreground"} />
                      </button>
                    );
                  })}
                </div>

                <div className="mt-4 grid gap-3">
                  <label className="grid gap-1">
                    <span className="text-xs font-semibold">What worked well?</span>
                    <textarea rows={2} value={worked} onChange={(e) => setWorked(e.target.value)}
                      className="rounded-xl border border-border bg-background p-2 text-sm"
                      data-testid="feedback-worked" />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-xs font-semibold">What was confusing?</span>
                    <textarea rows={2} value={confusing} onChange={(e) => setConfusing(e.target.value)}
                      className="rounded-xl border border-border bg-background p-2 text-sm"
                      data-testid="feedback-confusing" />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-xs font-semibold">What feature would you like next?</span>
                    <textarea rows={2} value={request} onChange={(e) => setRequest(e.target.value)}
                      className="rounded-xl border border-border bg-background p-2 text-sm"
                      data-testid="feedback-request" />
                  </label>
                </div>

                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="feedback-submit-btn"
                  className="mt-4 btn-primary w-full disabled:opacity-60"
                >
                  {busy ? <><Loader2 className="animate-spin" size={14} /> Sending…</> : "Send feedback"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
